import Link, { LinkProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import applyPropBuilder from "@insite/mobius/utilities/applyPropBuilder";
import { StyledProp } from "@insite/mobius/utilities/InjectableCss";
import injectCss from "@insite/mobius/utilities/injectCss";
import MobiusStyledComponentProps from "@insite/mobius/utilities/MobiusStyledComponentProps";
import * as React from "react";
import styled, { withTheme } from "styled-components";

export type BreadcrumbsProps = MobiusStyledComponentProps<
    "nav",
    {
        /** CSS string or styled-components function to be injected into this component.
         * @themable */
        css?: StyledProp<BreadcrumbsProps>;
        /** An array of link props */
        links?: LinkProps[];
        /**
         * Indicates how the `css` property is combined with the variant `css` property from the theme.
         * If true, the variant css is applied first and then the component css is applied after causing
         * a merge, much like normal CSS. If false, only the component css is applied, overriding the variant css in the theme.
         */
        mergeCss?: boolean;
        /**
         * Optional props to be passed down to all Link and Typography components.
         * Can be overriden by individual typographyProps in the links array.
         * @themable */
        typographyProps?: TypographyPresentationProps;
    }
>;

export type BreadcrumbsVariantPresentationProps = Omit<BreadcrumbsProps, "links">;

export const StyledNav = styled.nav.attrs({ "aria-label": "breadcrumbs" })`
    display: flex;
    flex-direction: row;
    align-items: center;
    ${injectCss}
`;

 export const Slash = styled(Typography as any).attrs({ children: "/" })`
    margin: 0 10px;
    display: block;
`;

const BreadCrumbsStaticVariant: React.FC<BreadcrumbsProps> = withTheme(({ links, mergeCss, ...otherProps }) => {
    const { spreadProps, applyStyledProp } = applyPropBuilder(otherProps, { component: "breadcrumbs" });
    const typographyProps = spreadProps("typographyProps");
    const resolvedMergeCss = mergeCss ?? otherProps?.theme?.breadcrumbs?.defaultProps?.mergeCss;

   

    if (!links || links.length === 0) {
        return null;
    }

    
	//Here We are keeping only first and last Link if we want to show all the links we can modify this code instead of filteredLinks use links
    const filteredLinks = [links[0], links[links.length - 1]];

    const children = filteredLinks.map((link: any, index: number) => {
        const linkChildren = link.children;
        if (!React.Children.count(linkChildren)) {
            return null;
        }
        const key = typeof linkChildren === "string" ? `${linkChildren}_${index}` : index;

        if (index === filteredLinks.length - 1) {
            return (
                <Typography
                    mergeCss={resolvedMergeCss}
                    {...typographyProps}
                    aria-current="page"
                    key={key}
                    data-test-selector={`breadCrumb_${linkChildren}`}
                >
                    {linkChildren}
                </Typography>
            );
        }
        return (
            <React.Fragment key={key}>
                <Link typographyProps={typographyProps} {...link} />
                <Slash {...typographyProps} />
            </React.Fragment>
        );
    });

    // Filter out any null values
    const validChildren = children.filter((child) => child !== null);

    if (validChildren.length === 0) {
        return null;
    }

    return (
        <StyledNav {...otherProps} css={applyStyledProp("css", resolvedMergeCss)}>
            {validChildren}
        </StyledNav>
    );
});



BreadCrumbsStaticVariant.displayName = "Breadcrumbs";
/** @component */
export default BreadCrumbsStaticVariant;
